import type { MeQuery } from '@/generated/graphql'

export type Permission = string

export enum PlatformPermission {
  GameCreate = 'game:create',
  Pii = 'pii',
}

export function hasPermission(
  user: MeQuery['me'] | undefined,
  permission: Permission,
  gameId?: string
): boolean {
  if (!user) return false

  if (user.isSuperAdmin) return true
  if (permission === PlatformPermission.GameCreate) return !!user.gameCreation
  if (permission === PlatformPermission.Pii) return !!user.pii

  const permissions = user.permissions
  if (!permissions) return false

  if (permissions.allGames?.includes(permission)) {
    return true
  }

  if (gameId && permissions.perGame?.[gameId]) {
    return permissions.perGame[gameId].includes(permission)
  }

  if (!gameId) {
    return Object.values(permissions.perGame || {}).some(
      (gamePermissions) =>
        Array.isArray(gamePermissions) && gamePermissions.includes(permission)
    )
  }

  return false
}

export function hasAnyPermission(
  user: MeQuery['me'] | undefined,
  permissions: Permission[],
  gameId?: string
): boolean {
  return permissions.some((permission) =>
    hasPermission(user, permission, gameId)
  )
}

export function hasAllPermissions(
  user: MeQuery['me'] | undefined,
  permissions: Permission[],
  gameId?: string
): boolean {
  return permissions.every((permission) =>
    hasPermission(user, permission, gameId)
  )
}

export function isGameAvailable(
  user: MeQuery['me'] | undefined,
  gameId: string
): boolean {
  if (!user) return false

  if (user.isSuperAdmin) return true

  const permissions = user.permissions
  if (!permissions) return false

  return !!permissions.allGames?.length || !!permissions.perGame?.[gameId]
}

export function hasGameAccess(user: MeQuery['me'] | undefined): boolean {
  if (!user) return false

  if (user.isSuperAdmin) return true

  const permissions = user.permissions
  if (!permissions) return false

  return (
    !!permissions.allGames?.length ||
    Object.keys(permissions.perGame || {}).length > 0
  )
}

export function getGamePermissions(
  user: MeQuery['me'] | undefined,
  gameId: string
): Permission[] {
  if (!user) return []

  if (user.isSuperAdmin) {
    return []
  }

  const permissions = user.permissions
  if (!permissions) return []

  return [
    ...(permissions.allGames || []),
    ...(permissions.perGame?.[gameId] || []),
  ]
}
